import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  clients: "Clients",
  clientsShows: "Total clients, and new this month",
  total_aum: "Total AUM",
  total_aumShows: "Cash and positions in synced accounts",
  total_aumShort: "AUM",
  saving_inflow: "Saving inflow",
  saving_inflowShows: "Active savings plans, per year",
  activities: "Activities",
  activitiesShows: "Meetings, calls, notes and flow events",
  flows: "Flows",
  flowsShows: "Flow instances by status",
  completed_this_month: "Completed this month",
  completed_this_monthShows: "Flows completed this month",
  completed_this_monthShort: "Completed",
  pending_signatures: "Pending signatures",
  pending_signaturesShows: "Pending and signed signatures",
  pending_signaturesShort: "Signatures",
  users: "Users",
  usersShows: "Top 15 users by AUM · sortable",
  pipeline: "Pipeline",
  pipelineShows: "Open flows by stage",
  next_meetings: "Next meetings",
  next_meetingsShows: "The user's next 5 meetings",
  action_items: "Action items",
  action_itemsShows: "Signatures, inactive flows, failed PDFs",
  recent_activity: "Recent activity",
  recent_activityShows: "Flow lifecycle events",
  signatures_expiring: "Signatures expiring",
  signatures_expiringShows: "Signing links expiring within 48 hours",
  cash_to_invest: "Cash to invest",
  cash_to_investShows: "Clients with more cash than the threshold",
  approvals_waiting: "Approvals waiting",
  approvals_waitingShows: "Flows stopped at the compliance gate",
  review_status: "Review status",
  review_statusShows: "Completed advice by compliance check",
  advice_to_check: "Advice to check",
  advice_to_checkShows: "Completed flows not yet approved",
  screening: "Screening",
  screeningShows: "PEP and sanctions standing",
  verified_id: "Verified ID",
  verified_idShows: "Clients with and without Verified ID",
  identity_changes: "Identity changes",
  identity_changesShows: "Changes that void Verified ID and screening",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    clients: "Clients",
    clientsShows: "Nombre total de clients et nouveaux clients ce mois-ci",
    total_aum: "Encours sous gestion",
    total_aumShows: "Liquidités et positions des comptes synchronisés",
    total_aumShort: "Encours",
    saving_inflow: "Épargne",
    saving_inflowShows: "Plans d'épargne actifs, par an",
    activities: "Activités",
    activitiesShows: "Rendez-vous, appels, notes et événements de parcours",
    flows: "Parcours",
    flowsShows: "Instances de parcours par statut",
    completed_this_month: "Terminés ce mois-ci",
    completed_this_monthShows: "Parcours terminés ce mois-ci",
    completed_this_monthShort: "Terminés",
    pending_signatures: "Signatures en attente",
    pending_signaturesShows: "Signatures en attente et signées",
    pending_signaturesShort: "Signatures",
    users: "Utilisateurs",
    usersShows: "Top 15 des utilisateurs par encours · triable",
    pipeline: "Pipeline",
    pipelineShows: "Parcours ouverts par étape",
    next_meetings: "Prochains rendez-vous",
    next_meetingsShows: "Les 5 prochains rendez-vous de l'utilisateur",
    action_items: "Actions à mener",
    action_itemsShows: "Signatures, parcours inactifs, échecs de PDF",
    recent_activity: "Activité récente",
    recent_activityShows: "Événements du cycle de vie des parcours",
    signatures_expiring: "Signatures arrivant à expiration",
    signatures_expiringShows: "Liens de signature qui expirent dans les 48 heures",
    cash_to_invest: "Liquidités à investir",
    cash_to_investShows: "Clients dont les liquidités dépassent le seuil",
    approvals_waiting: "Approbations en attente",
    approvals_waitingShows: "Parcours bloqués au contrôle de conformité",
    review_status: "Statut de revue",
    review_statusShows: "Conseils terminés selon le contrôle de conformité",
    advice_to_check: "Conseils à contrôler",
    advice_to_checkShows: "Parcours terminés pas encore approuvés",
    screening: "Filtrage",
    screeningShows: "Statut PEP et sanctions",
    verified_id: "Identité vérifiée",
    verified_idShows: "Clients avec et sans identité vérifiée",
    identity_changes: "Modifications d'identité",
    identity_changesShows: "Modifications qui annulent l'identité vérifiée et le filtrage",
  },
  es_ES: {
    clients: "Clientes",
    clientsShows: "Total de clientes y nuevos este mes",
    total_aum: "Patrimonio gestionado",
    total_aumShows: "Efectivo y posiciones en cuentas sincronizadas",
    total_aumShort: "Patrimonio",
    saving_inflow: "Ahorro",
    saving_inflowShows: "Planes de ahorro activos, por año",
    activities: "Actividades",
    activitiesShows: "Reuniones, llamadas, notas y eventos de flujo",
    flows: "Flujos",
    flowsShows: "Instancias de flujo por estado",
    completed_this_month: "Completados este mes",
    completed_this_monthShows: "Flujos completados este mes",
    completed_this_monthShort: "Completados",
    pending_signatures: "Firmas pendientes",
    pending_signaturesShows: "Firmas pendientes y firmadas",
    pending_signaturesShort: "Firmas",
    users: "Usuarios",
    usersShows: "Top 15 de usuarios por patrimonio gestionado · ordenable",
    pipeline: "Pipeline",
    pipelineShows: "Flujos abiertos por etapa",
    next_meetings: "Próximas reuniones",
    next_meetingsShows: "Las 5 próximas reuniones del usuario",
    action_items: "Tareas pendientes",
    action_itemsShows: "Firmas, flujos inactivos, PDF fallidos",
    recent_activity: "Actividad reciente",
    recent_activityShows: "Eventos del ciclo de vida del flujo",
    signatures_expiring: "Firmas a punto de caducar",
    signatures_expiringShows: "Enlaces de firma que caducan en las próximas 48 horas",
    cash_to_invest: "Efectivo para invertir",
    cash_to_investShows: "Clientes con más efectivo que el umbral",
    approvals_waiting: "Aprobaciones pendientes",
    approvals_waitingShows: "Flujos detenidos en el control de cumplimiento",
    review_status: "Estado de revisión",
    review_statusShows: "Asesoramientos completados según el control de cumplimiento",
    advice_to_check: "Asesoramientos por revisar",
    advice_to_checkShows: "Flujos completados aún no aprobados",
    screening: "Screening",
    screeningShows: "Situación de PEP y sanciones",
    verified_id: "Identidad verificada",
    verified_idShows: "Clientes con y sin identidad verificada",
    identity_changes: "Cambios de identidad",
    identity_changesShows: "Cambios que anulan la identidad verificada y el screening",
  },
  pt_BR: {
    clients: "Clientes",
    clientsShows: "Total de clientes e novos neste mês",
    total_aum: "Patrimônio sob gestão",
    total_aumShows: "Caixa e posições em contas sincronizadas",
    total_aumShort: "Patrimônio",
    saving_inflow: "Aportes",
    saving_inflowShows: "Planos de aportes ativos, por ano",
    activities: "Atividades",
    activitiesShows: "Reuniões, ligações, notas e eventos de fluxo",
    flows: "Fluxos",
    flowsShows: "Instâncias de fluxo por status",
    completed_this_month: "Concluídos este mês",
    completed_this_monthShows: "Fluxos concluídos este mês",
    completed_this_monthShort: "Concluídos",
    pending_signatures: "Assinaturas pendentes",
    pending_signaturesShows: "Assinaturas pendentes e concluídas",
    pending_signaturesShort: "Assinaturas",
    users: "Usuários",
    usersShows: "Top 15 usuários por patrimônio sob gestão · ordenável",
    pipeline: "Pipeline",
    pipelineShows: "Fluxos abertos por etapa",
    next_meetings: "Próximas reuniões",
    next_meetingsShows: "As próximas 5 reuniões do usuário",
    action_items: "Itens de ação",
    action_itemsShows: "Assinaturas, fluxos inativos, PDFs com falha",
    recent_activity: "Atividade recente",
    recent_activityShows: "Eventos do ciclo de vida do fluxo",
    signatures_expiring: "Assinaturas a expirar",
    signatures_expiringShows: "Links de assinatura que expiram em até 48 horas",
    cash_to_invest: "Caixa para investir",
    cash_to_investShows: "Clientes com mais caixa do que o limite",
    approvals_waiting: "Aprovações pendentes",
    approvals_waitingShows: "Fluxos parados na etapa de compliance",
    review_status: "Status da revisão",
    review_statusShows: "Recomendações concluídas por verificação de compliance",
    advice_to_check: "Recomendações a verificar",
    advice_to_checkShows: "Fluxos concluídos ainda não aprovados",
    screening: "Triagem",
    screeningShows: "Situação de PEP e sanções",
    verified_id: "Identidade verificada",
    verified_idShows: "Clientes com e sem identidade verificada",
    identity_changes: "Alterações de identidade",
    identity_changesShows: "Alterações que invalidam a identidade verificada e a triagem",
  },
  de_DE: {
    clients: "Kunden",
    clientsShows: "Kunden gesamt und Neukunden in diesem Monat",
    total_aum: "Verwaltetes Vermögen",
    total_aumShows: "Liquidität und Positionen in synchronisierten Konten",
    total_aumShort: "Vermögen",
    saving_inflow: "Sparzuflüsse",
    saving_inflowShows: "Aktive Sparpläne, pro Jahr",
    activities: "Aktivitäten",
    activitiesShows: "Termine, Anrufe, Notizen und Flow-Ereignisse",
    flows: "Flows",
    flowsShows: "Flow-Instanzen nach Status",
    completed_this_month: "Diesen Monat abgeschlossen",
    completed_this_monthShows: "In diesem Monat abgeschlossene Flows",
    completed_this_monthShort: "Abgeschlossen",
    pending_signatures: "Ausstehende Signaturen",
    pending_signaturesShows: "Ausstehende und erfolgte Signaturen",
    pending_signaturesShort: "Signaturen",
    users: "Benutzer",
    usersShows: "Top 15 Benutzer nach verwaltetem Vermögen · sortierbar",
    pipeline: "Pipeline",
    pipelineShows: "Offene Flows nach Phase",
    next_meetings: "Nächste Termine",
    next_meetingsShows: "Die nächsten 5 Termine des Benutzers",
    action_items: "Offene Aufgaben",
    action_itemsShows: "Signaturen, inaktive Flows, fehlgeschlagene PDFs",
    recent_activity: "Letzte Aktivitäten",
    recent_activityShows: "Ereignisse im Flow-Lebenszyklus",
    signatures_expiring: "Ablaufende Signaturen",
    signatures_expiringShows: "Signaturlinks, die innerhalb von 48 Stunden ablaufen",
    cash_to_invest: "Liquidität zum Investieren",
    cash_to_investShows: "Kunden mit mehr Liquidität als der Schwellenwert",
    approvals_waiting: "Ausstehende Freigaben",
    approvals_waitingShows: "Flows, die an der Compliance-Freigabe angehalten wurden",
    review_status: "Prüfstatus",
    review_statusShows: "Abgeschlossene Beratungen nach Compliance-Prüfung",
    advice_to_check: "Zu prüfende Beratungen",
    advice_to_checkShows: "Abgeschlossene, noch nicht freigegebene Flows",
    screening: "Screening",
    screeningShows: "PEP- und Sanktionsstatus",
    verified_id: "Verifizierte Identität",
    verified_idShows: "Kunden mit und ohne verifizierte Identität",
    identity_changes: "Identitätsänderungen",
    identity_changesShows: "Änderungen, die verifizierte Identität und Screening aufheben",
  },
  it_IT: {
    clients: "Clienti",
    clientsShows: "Totale clienti e nuovi di questo mese",
    total_aum: "Patrimonio gestito",
    total_aumShows: "Liquidità e posizioni nei conti sincronizzati",
    total_aumShort: "Patrimonio",
    saving_inflow: "Flussi di risparmio",
    saving_inflowShows: "Piani di accumulo attivi, per anno",
    activities: "Attività",
    activitiesShows: "Riunioni, chiamate, note ed eventi di flusso",
    flows: "Flussi",
    flowsShows: "Istanze di flusso per stato",
    completed_this_month: "Completati questo mese",
    completed_this_monthShows: "Flussi completati questo mese",
    completed_this_monthShort: "Completati",
    pending_signatures: "Firme in attesa",
    pending_signaturesShows: "Firme in attesa e completate",
    pending_signaturesShort: "Firme",
    users: "Utenti",
    usersShows: "Top 15 utenti per patrimonio gestito · ordinabile",
    pipeline: "Pipeline",
    pipelineShows: "Flussi aperti per fase",
    next_meetings: "Prossime riunioni",
    next_meetingsShows: "Le prossime 5 riunioni dell'utente",
    action_items: "Azioni da svolgere",
    action_itemsShows: "Firme, flussi inattivi, PDF non riusciti",
    recent_activity: "Attività recenti",
    recent_activityShows: "Eventi del ciclo di vita del flusso",
    signatures_expiring: "Firme in scadenza",
    signatures_expiringShows: "Link di firma che scadono entro 48 ore",
    cash_to_invest: "Liquidità da investire",
    cash_to_investShows: "Clienti con liquidità superiore alla soglia",
    approvals_waiting: "Approvazioni in attesa",
    approvals_waitingShows: "Flussi fermi al controllo di compliance",
    review_status: "Stato di revisione",
    review_statusShows: "Consulenze completate per controllo di compliance",
    advice_to_check: "Consulenze da verificare",
    advice_to_checkShows: "Flussi completati non ancora approvati",
    screening: "Screening",
    screeningShows: "Stato PEP e sanzioni",
    verified_id: "Identità verificata",
    verified_idShows: "Clienti con e senza identità verificata",
    identity_changes: "Modifiche dell'identità",
    identity_changesShows: "Modifiche che annullano l'identità verificata e lo screening",
  },
  ar: {
    clients: "العملاء",
    clientsShows: "إجمالي العملاء، والجدد هذا الشهر",
    total_aum: "إجمالي الأصول المُدارة",
    total_aumShows: "النقد والمراكز في الحسابات المتزامنة",
    total_aumShort: "الأصول",
    saving_inflow: "تدفقات الادخار",
    saving_inflowShows: "خطط الادخار النشطة، سنويًا",
    activities: "الأنشطة",
    activitiesShows: "الاجتماعات والمكالمات والملاحظات وأحداث المسارات",
    flows: "المسارات",
    flowsShows: "نُسخ المسارات حسب الحالة",
    completed_this_month: "المكتمل هذا الشهر",
    completed_this_monthShows: "المسارات المكتملة هذا الشهر",
    completed_this_monthShort: "مكتمل",
    pending_signatures: "التوقيعات المعلّقة",
    pending_signaturesShows: "التوقيعات المعلّقة والموقّعة",
    pending_signaturesShort: "التوقيعات",
    users: "المستخدمون",
    usersShows: "أعلى 15 مستخدمًا حسب الأصول المُدارة · قابلة للترتيب",
    pipeline: "خط سير العمل",
    pipelineShows: "المسارات المفتوحة حسب المرحلة",
    next_meetings: "الاجتماعات القادمة",
    next_meetingsShows: "الاجتماعات الخمسة القادمة للمستخدم",
    action_items: "إجراءات مطلوبة",
    action_itemsShows: "التوقيعات، والمسارات غير النشطة، وملفات PDF الفاشلة",
    recent_activity: "النشاط الأخير",
    recent_activityShows: "أحداث دورة حياة المسار",
    signatures_expiring: "توقيعات على وشك الانتهاء",
    signatures_expiringShows: "روابط التوقيع الإلكتروني التي تنتهي خلال 48 ساعة",
    cash_to_invest: "نقد متاح للاستثمار",
    cash_to_investShows: "العملاء الذين لديهم نقد يتجاوز الحد",
    approvals_waiting: "موافقات بالانتظار",
    approvals_waitingShows: "مسارات متوقفة عند بوابة الامتثال",
    review_status: "حالة المراجعة",
    review_statusShows: "الاستشارات المكتملة حسب فحص الامتثال",
    advice_to_check: "استشارات للفحص",
    advice_to_checkShows: "مسارات مكتملة لم تُعتمد بعد",
    screening: "فحص القوائم",
    screeningShows: "وضع PEP والعقوبات",
    verified_id: "هوية موثّقة",
    verified_idShows: "العملاء بهوية موثّقة وبدونها",
    identity_changes: "تغييرات الهوية",
    identity_changesShows: "تغييرات تُلغي الهوية الموثّقة وفحص القوائم",
  },
  ja_JP: {
    clients: "顧客",
    clientsShows: "顧客総数と今月の新規顧客",
    total_aum: "運用資産残高（AUM）合計",
    total_aumShows: "同期済み口座の現金およびポジション",
    total_aumShort: "AUM",
    saving_inflow: "積立流入額",
    saving_inflowShows: "有効な積立プラン（年額）",
    activities: "アクティビティ",
    activitiesShows: "面談、通話、メモ、フローイベント",
    flows: "フロー",
    flowsShows: "ステータス別のフローインスタンス",
    completed_this_month: "今月の完了",
    completed_this_monthShows: "今月完了したフロー",
    completed_this_monthShort: "完了",
    pending_signatures: "署名待ち",
    pending_signaturesShows: "署名待ちおよび署名済みの署名",
    pending_signaturesShort: "署名",
    users: "ユーザー",
    usersShows: "AUM 上位 15 名のユーザー · 並べ替え可能",
    pipeline: "パイプライン",
    pipelineShows: "段階別の進行中フロー",
    next_meetings: "今後の面談",
    next_meetingsShows: "ユーザーの直近の面談 5 件",
    action_items: "対応事項",
    action_itemsShows: "署名、非アクティブなフロー、失敗した PDF",
    recent_activity: "最近のアクティビティ",
    recent_activityShows: "フローのライフサイクルイベント",
    signatures_expiring: "期限切れ間近の署名",
    signatures_expiringShows: "48 時間以内に期限切れとなる電子署名リンク",
    cash_to_invest: "投資可能な現金",
    cash_to_investShows: "現金がしきい値を超える顧客",
    approvals_waiting: "承認待ち",
    approvals_waitingShows: "コンプライアンス承認で停止中のフロー",
    review_status: "レビュー状況",
    review_statusShows: "コンプライアンスチェック別の完了済みアドバイス",
    advice_to_check: "チェック対象のアドバイス",
    advice_to_checkShows: "未承認の完了済みフロー",
    screening: "スクリーニング",
    screeningShows: "PEP および制裁の状況",
    verified_id: "本人確認",
    verified_idShows: "本人確認済み・未確認の顧客",
    identity_changes: "本人情報の変更",
    identity_changesShows: "本人確認とスクリーニングを無効にする変更",
  },
  hi_IN: {
    clients: "क्लाइंट",
    clientsShows: "कुल क्लाइंट और इस माह के नए क्लाइंट",
    total_aum: "कुल प्रबंधनाधीन परिसंपत्तियाँ (AUM)",
    total_aumShows: "सिंक किए गए खातों में नकद और पोज़िशन",
    total_aumShort: "AUM",
    saving_inflow: "बचत अंतर्वाह",
    saving_inflowShows: "सक्रिय बचत योजनाएँ, प्रति वर्ष",
    activities: "गतिविधियाँ",
    activitiesShows: "मीटिंग, कॉल, नोट्स और फ़्लो इवेंट",
    flows: "फ़्लो",
    flowsShows: "स्थिति के अनुसार फ़्लो इंस्टेंस",
    completed_this_month: "इस माह पूर्ण",
    completed_this_monthShows: "इस माह पूर्ण हुए फ़्लो",
    completed_this_monthShort: "पूर्ण",
    pending_signatures: "लंबित हस्ताक्षर",
    pending_signaturesShows: "लंबित और पूर्ण हस्ताक्षर",
    pending_signaturesShort: "हस्ताक्षर",
    users: "उपयोगकर्ता",
    usersShows: "AUM के अनुसार शीर्ष 15 उपयोगकर्ता · क्रमबद्ध करने योग्य",
    pipeline: "पाइपलाइन",
    pipelineShows: "चरण के अनुसार खुले फ़्लो",
    next_meetings: "आगामी मीटिंग",
    next_meetingsShows: "उपयोगकर्ता की अगली 5 मीटिंग",
    action_items: "कार्य आइटम",
    action_itemsShows: "हस्ताक्षर, निष्क्रिय फ़्लो, विफल PDF",
    recent_activity: "हाल की गतिविधि",
    recent_activityShows: "फ़्लो जीवनचक्र इवेंट",
    signatures_expiring: "समाप्त हो रहे हस्ताक्षर",
    signatures_expiringShows: "48 घंटे के भीतर समाप्त होने वाले ई-हस्ताक्षर लिंक",
    cash_to_invest: "निवेश योग्य नकद",
    cash_to_investShows: "सीमा से अधिक नकद वाले क्लाइंट",
    approvals_waiting: "लंबित अनुमोदन",
    approvals_waitingShows: "अनुपालन चरण पर रुके फ़्लो",
    review_status: "समीक्षा स्थिति",
    review_statusShows: "अनुपालन जाँच के अनुसार पूर्ण सलाह",
    advice_to_check: "जाँच हेतु सलाह",
    advice_to_checkShows: "पूर्ण फ़्लो जो अभी अनुमोदित नहीं हुए",
    screening: "स्क्रीनिंग",
    screeningShows: "PEP और प्रतिबंध स्थिति",
    verified_id: "सत्यापित पहचान",
    verified_idShows: "सत्यापित पहचान वाले और बिना सत्यापित पहचान वाले क्लाइंट",
    identity_changes: "पहचान में बदलाव",
    identity_changesShows: "बदलाव जो सत्यापित पहचान और स्क्रीनिंग को अमान्य करते हैं",
  },
  zh_CN: {
    clients: "客户",
    clientsShows: "客户总数及本月新增",
    total_aum: "管理资产规模 (AUM) 总计",
    total_aumShows: "已同步账户中的现金及持仓",
    total_aumShort: "AUM",
    saving_inflow: "定投流入",
    saving_inflowShows: "有效定投计划，按年计",
    activities: "活动",
    activitiesShows: "会议、通话、备注和流程事件",
    flows: "流程",
    flowsShows: "按状态划分的流程实例",
    completed_this_month: "本月完成",
    completed_this_monthShows: "本月完成的流程",
    completed_this_monthShort: "已完成",
    pending_signatures: "待签署",
    pending_signaturesShows: "待签署和已签署的签名",
    pending_signaturesShort: "签名",
    users: "用户",
    usersShows: "按 AUM 排名前 15 的用户 · 可排序",
    pipeline: "流程管道",
    pipelineShows: "按阶段划分的进行中流程",
    next_meetings: "即将召开的会议",
    next_meetingsShows: "该用户接下来的 5 场会议",
    action_items: "待办事项",
    action_itemsShows: "签署、非活跃流程、失败的 PDF",
    recent_activity: "最近活动",
    recent_activityShows: "流程生命周期事件",
    signatures_expiring: "即将过期的签署",
    signatures_expiringShows: "48 小时内过期的电子签名链接",
    cash_to_invest: "可投资现金",
    cash_to_investShows: "现金超过阈值的客户",
    approvals_waiting: "待审批",
    approvals_waitingShows: "停在合规审批环节的流程",
    review_status: "审核状态",
    review_statusShows: "按合规检查划分的已完成建议",
    advice_to_check: "待检查的建议",
    advice_to_checkShows: "尚未批准的已完成流程",
    screening: "筛查",
    screeningShows: "PEP 与制裁状态",
    verified_id: "已验证身份",
    verified_idShows: "已验证和未验证身份的客户",
    identity_changes: "身份信息变更",
    identity_changesShows: "会使已验证身份和筛查失效的变更",
  },
  zh_TW: {
    clients: "客戶",
    clientsShows: "客戶總數及本月新增",
    total_aum: "管理資產規模 (AUM) 總計",
    total_aumShows: "已同步帳戶中的現金及持有部位",
    total_aumShort: "AUM",
    saving_inflow: "定期定額流入",
    saving_inflowShows: "有效的定期定額計畫，以年計",
    activities: "活動",
    activitiesShows: "會議、通話、備註與流程事件",
    flows: "流程",
    flowsShows: "依狀態區分的流程執行個體",
    completed_this_month: "本月完成",
    completed_this_monthShows: "本月完成的流程",
    completed_this_monthShort: "已完成",
    pending_signatures: "待簽署",
    pending_signaturesShows: "待簽署與已簽署的簽名",
    pending_signaturesShort: "簽名",
    users: "使用者",
    usersShows: "依 AUM 排名前 15 的使用者 · 可排序",
    pipeline: "流程進度",
    pipelineShows: "依階段區分的進行中流程",
    next_meetings: "即將舉行的會議",
    next_meetingsShows: "該使用者接下來的 5 場會議",
    action_items: "待辦事項",
    action_itemsShows: "簽署、非活躍流程、失敗的 PDF",
    recent_activity: "近期活動",
    recent_activityShows: "流程生命週期事件",
    signatures_expiring: "即將到期的簽署",
    signatures_expiringShows: "48 小時內到期的電子簽名連結",
    cash_to_invest: "可投資現金",
    cash_to_investShows: "現金超過門檻的客戶",
    approvals_waiting: "待核准",
    approvals_waitingShows: "停在合規核准關卡的流程",
    review_status: "審核狀態",
    review_statusShows: "依合規檢查區分的已完成建議",
    advice_to_check: "待檢查的建議",
    advice_to_checkShows: "尚未核准的已完成流程",
    screening: "篩檢",
    screeningShows: "PEP 與制裁狀態",
    verified_id: "已驗證身分",
    verified_idShows: "已驗證與未驗證身分的客戶",
    identity_changes: "身分資料變更",
    identity_changesShows: "會使已驗證身分與篩檢失效的變更",
  },
  nl_NL: {
    clients: "Klanten",
    clientsShows: "Totaal aantal klanten en nieuwe deze maand",
    total_aum: "Totaal beheerd vermogen",
    total_aumShows: "Liquide middelen en posities op gesynchroniseerde rekeningen",
    total_aumShort: "Vermogen",
    saving_inflow: "Spaarinleg",
    saving_inflowShows: "Actieve spaarplannen, per jaar",
    activities: "Activiteiten",
    activitiesShows: "Afspraken, gesprekken, notities en flowgebeurtenissen",
    flows: "Flows",
    flowsShows: "Flowinstanties per status",
    completed_this_month: "Voltooid deze maand",
    completed_this_monthShows: "Flows voltooid deze maand",
    completed_this_monthShort: "Voltooid",
    pending_signatures: "Openstaande ondertekeningen",
    pending_signaturesShows: "Openstaande en voltooide ondertekeningen",
    pending_signaturesShort: "Onderteken.",
    users: "Gebruikers",
    usersShows: "Top 15 gebruikers op beheerd vermogen · sorteerbaar",
    pipeline: "Pipeline",
    pipelineShows: "Open flows per fase",
    next_meetings: "Komende afspraken",
    next_meetingsShows: "De volgende 5 afspraken van de gebruiker",
    action_items: "Actiepunten",
    action_itemsShows: "Ondertekeningen, inactieve flows, mislukte pdf's",
    recent_activity: "Recente activiteit",
    recent_activityShows: "Gebeurtenissen in de levenscyclus van flows",
    signatures_expiring: "Verlopende ondertekeningen",
    signatures_expiringShows: "Ondertekeningslinks die binnen 48 uur verlopen",
    cash_to_invest: "Te beleggen liquiditeit",
    cash_to_investShows: "Klanten met meer liquide middelen dan de drempel",
    approvals_waiting: "Wachtend op goedkeuring",
    approvals_waitingShows: "Flows die bij de compliancecontrole zijn gestopt",
    review_status: "Beoordelingsstatus",
    review_statusShows: "Voltooid advies per compliancecontrole",
    advice_to_check: "Te controleren advies",
    advice_to_checkShows: "Voltooide flows die nog niet zijn goedgekeurd",
    screening: "Screening",
    screeningShows: "PEP- en sanctiestatus",
    verified_id: "Geverifieerde identiteit",
    verified_idShows: "Klanten met en zonder geverifieerde identiteit",
    identity_changes: "Identiteitswijzigingen",
    identity_changesShows:
      "Wijzigingen die de geverifieerde identiteit en screening ongeldig maken",
  },
  nb_NO: {
    clients: "Kunder",
    clientsShows: "Totalt antall kunder, og nye denne måneden",
    total_aum: "Total forvaltningskapital",
    total_aumShows: "Kontanter og posisjoner i synkroniserte kontoer",
    total_aumShort: "Forvaltning",
    saving_inflow: "Sparing",
    saving_inflowShows: "Aktive spareavtaler, per år",
    activities: "Aktiviteter",
    activitiesShows: "Møter, samtaler, notater og flythendelser",
    flows: "Flyter",
    flowsShows: "Flytinstanser etter status",
    completed_this_month: "Fullført denne måneden",
    completed_this_monthShows: "Flyter fullført denne måneden",
    completed_this_monthShort: "Fullført",
    pending_signatures: "Ventende signaturer",
    pending_signaturesShows: "Ventende og signerte signaturer",
    pending_signaturesShort: "Signaturer",
    users: "Brukere",
    usersShows: "Topp 15 brukere etter forvaltningskapital · sorterbar",
    pipeline: "Pipeline",
    pipelineShows: "Åpne flyter etter steg",
    next_meetings: "Neste møter",
    next_meetingsShows: "Brukerens neste 5 møter",
    action_items: "Handlingspunkter",
    action_itemsShows: "Signaturer, inaktive flyter, PDF-feil",
    recent_activity: "Siste aktivitet",
    recent_activityShows: "Hendelser i flytens livsløp",
    signatures_expiring: "Signaturer som utløper",
    signatures_expiringShows: "Signeringslenker som utløper innen 48 timer",
    cash_to_invest: "Kontanter å investere",
    cash_to_investShows: "Kunder med mer kontanter enn terskelen",
    approvals_waiting: "Venter på godkjenning",
    approvals_waitingShows: "Flyter stoppet ved compliance-kontrollen",
    review_status: "Gjennomgangsstatus",
    review_statusShows: "Fullførte råd etter compliance-kontroll",
    advice_to_check: "Råd å kontrollere",
    advice_to_checkShows: "Fullførte flyter som ikke er godkjent ennå",
    screening: "Screening",
    screeningShows: "PEP- og sanksjonsstatus",
    verified_id: "Verifisert ID",
    verified_idShows: "Kunder med og uten verifisert ID",
    identity_changes: "Identitetsendringer",
    identity_changesShows: "Endringer som opphever verifisert ID og screening",
  },
  sv_SE: {
    clients: "Kunder",
    clientsShows: "Totalt antal kunder och nya denna månad",
    total_aum: "Totalt förvaltat kapital",
    total_aumShows: "Likvida medel och innehav på synkade konton",
    total_aumShort: "Kapital",
    saving_inflow: "Sparande",
    saving_inflowShows: "Aktiva sparavtal, per år",
    activities: "Aktiviteter",
    activitiesShows: "Möten, samtal, anteckningar och flödeshändelser",
    flows: "Flöden",
    flowsShows: "Flödesinstanser efter status",
    completed_this_month: "Slutförda denna månad",
    completed_this_monthShows: "Flöden som slutförts denna månad",
    completed_this_monthShort: "Slutförda",
    pending_signatures: "Väntande signaturer",
    pending_signaturesShows: "Väntande och signerade signaturer",
    pending_signaturesShort: "Signaturer",
    users: "Användare",
    usersShows: "Topp 15 användare efter förvaltat kapital · sorterbar",
    pipeline: "Pipeline",
    pipelineShows: "Öppna flöden per steg",
    next_meetings: "Kommande möten",
    next_meetingsShows: "Användarens 5 närmaste möten",
    action_items: "Att göra",
    action_itemsShows: "Signaturer, inaktiva flöden, misslyckade PDF:er",
    recent_activity: "Senaste aktivitet",
    recent_activityShows: "Händelser i flödets livscykel",
    signatures_expiring: "Signaturer som löper ut",
    signatures_expiringShows: "Signeringslänkar som löper ut inom 48 timmar",
    cash_to_invest: "Likvida medel att investera",
    cash_to_investShows: "Kunder med mer likvida medel än tröskelvärdet",
    approvals_waiting: "Väntar på godkännande",
    approvals_waitingShows: "Flöden som stoppats vid compliance-kontrollen",
    review_status: "Granskningsstatus",
    review_statusShows: "Slutförd rådgivning efter compliance-kontroll",
    advice_to_check: "Rådgivning att kontrollera",
    advice_to_checkShows: "Slutförda flöden som ännu inte godkänts",
    screening: "Screening",
    screeningShows: "PEP- och sanktionsstatus",
    verified_id: "Verifierat ID",
    verified_idShows: "Kunder med och utan verifierat ID",
    identity_changes: "Identitetsändringar",
    identity_changesShows: "Ändringar som upphäver verifierat ID och screening",
  },
  fi_FI: {
    clients: "Asiakkaat",
    clientsShows: "Asiakkaat yhteensä ja uudet tässä kuussa",
    total_aum: "Hallinnoitavat varat yhteensä",
    total_aumShows: "Käteinen ja positiot synkronoiduilla tileillä",
    total_aumShort: "Varat",
    saving_inflow: "Säästöt",
    saving_inflowShows: "Aktiiviset säästösopimukset, vuodessa",
    activities: "Toiminnot",
    activitiesShows: "Tapaamiset, puhelut, muistiinpanot ja prosessitapahtumat",
    flows: "Prosessit",
    flowsShows: "Prosessi-instanssit tilan mukaan",
    completed_this_month: "Valmistuneet tässä kuussa",
    completed_this_monthShows: "Tässä kuussa valmistuneet prosessit",
    completed_this_monthShort: "Valmiit",
    pending_signatures: "Odottavat allekirjoitukset",
    pending_signaturesShows: "Odottavat ja tehdyt allekirjoitukset",
    pending_signaturesShort: "Allekirj.",
    users: "Käyttäjät",
    usersShows: "15 suurinta käyttäjää hallinnoitavien varojen mukaan · lajiteltava",
    pipeline: "Putki",
    pipelineShows: "Avoimet prosessit vaiheittain",
    next_meetings: "Tulevat tapaamiset",
    next_meetingsShows: "Käyttäjän seuraavat 5 tapaamista",
    action_items: "Tehtävät",
    action_itemsShows: "Allekirjoitukset, passiiviset prosessit, epäonnistuneet PDF:t",
    recent_activity: "Viimeaikainen toiminta",
    recent_activityShows: "Prosessin elinkaaren tapahtumat",
    signatures_expiring: "Vanhenevat allekirjoitukset",
    signatures_expiringShows: "48 tunnin kuluessa vanhenevat allekirjoituslinkit",
    cash_to_invest: "Sijoitettava käteinen",
    cash_to_investShows: "Asiakkaat, joilla on käteistä yli raja-arvon",
    approvals_waiting: "Odottaa hyväksyntää",
    approvals_waitingShows: "Compliance-tarkastukseen pysähtyneet prosessit",
    review_status: "Tarkastuksen tila",
    review_statusShows: "Valmis neuvonta compliance-tarkastuksen mukaan",
    advice_to_check: "Tarkastettava neuvonta",
    advice_to_checkShows: "Valmistuneet prosessit, joita ei ole vielä hyväksytty",
    screening: "Seulonta",
    screeningShows: "PEP- ja pakotetilanne",
    verified_id: "Vahvistettu henkilöllisyys",
    verified_idShows: "Asiakkaat, joiden henkilöllisyys on tai ei ole vahvistettu",
    identity_changes: "Henkilötietomuutokset",
    identity_changesShows: "Muutokset, jotka mitätöivät vahvistetun henkilöllisyyden ja seulonnan",
  },
  da_DK: {
    clients: "Kunder",
    clientsShows: "Kunder i alt og nye denne måned",
    total_aum: "Samlet formue under forvaltning",
    total_aumShows: "Kontanter og positioner på synkroniserede konti",
    total_aumShort: "Formue",
    saving_inflow: "Opsparing",
    saving_inflowShows: "Aktive opsparingsaftaler, pr. år",
    activities: "Aktiviteter",
    activitiesShows: "Møder, opkald, noter og forløbshændelser",
    flows: "Forløb",
    flowsShows: "Forløbsinstanser efter status",
    completed_this_month: "Gennemført denne måned",
    completed_this_monthShows: "Forløb gennemført denne måned",
    completed_this_monthShort: "Gennemført",
    pending_signatures: "Afventende signaturer",
    pending_signaturesShows: "Afventende og underskrevne signaturer",
    pending_signaturesShort: "Signaturer",
    users: "Brugere",
    usersShows: "Top 15 brugere efter formue under forvaltning · kan sorteres",
    pipeline: "Pipeline",
    pipelineShows: "Åbne forløb efter trin",
    next_meetings: "Kommende møder",
    next_meetingsShows: "Brugerens næste 5 møder",
    action_items: "Opgaver",
    action_itemsShows: "Signaturer, inaktive forløb, mislykkede PDF'er",
    recent_activity: "Seneste aktivitet",
    recent_activityShows: "Hændelser i forløbets livscyklus",
    signatures_expiring: "Signaturer, der udløber",
    signatures_expiringShows: "Signeringslinks, der udløber inden for 48 timer",
    cash_to_invest: "Kontanter til investering",
    cash_to_investShows: "Kunder med flere kontanter end grænsen",
    approvals_waiting: "Afventer godkendelse",
    approvals_waitingShows: "Forløb stoppet ved compliance-kontrollen",
    review_status: "Gennemgangsstatus",
    review_statusShows: "Gennemført rådgivning efter compliance-kontrol",
    advice_to_check: "Rådgivning til kontrol",
    advice_to_checkShows: "Gennemførte forløb, der endnu ikke er godkendt",
    screening: "Screening",
    screeningShows: "PEP- og sanktionsstatus",
    verified_id: "Verificeret ID",
    verified_idShows: "Kunder med og uden verificeret ID",
    identity_changes: "Identitetsændringer",
    identity_changesShows: "Ændringer, der ophæver verificeret ID og screening",
  },
  is_IS: {
    clients: "Viðskiptavinir",
    clientsShows: "Viðskiptavinir alls og nýir í þessum mánuði",
    total_aum: "Eignir í stýringu alls",
    total_aumShows: "Reiðufé og stöður á samstilltum reikningum",
    total_aumShort: "Eignir",
    saving_inflow: "Sparnaður",
    saving_inflowShows: "Virkir sparnaðarsamningar, á ári",
    activities: "Aðgerðir",
    activitiesShows: "Fundir, símtöl, minnispunktar og ferlisatburðir",
    flows: "Ferli",
    flowsShows: "Ferlistilvik eftir stöðu",
    completed_this_month: "Lokið í þessum mánuði",
    completed_this_monthShows: "Ferli sem lauk í þessum mánuði",
    completed_this_monthShort: "Lokið",
    pending_signatures: "Undirritanir í bið",
    pending_signaturesShows: "Undirritanir í bið og lokið",
    pending_signaturesShort: "Undirritanir",
    users: "Notendur",
    usersShows: "Efstu 15 notendur eftir eignum í stýringu · hægt að raða",
    pipeline: "Verkflæði",
    pipelineShows: "Opin ferli eftir stigi",
    next_meetings: "Næstu fundir",
    next_meetingsShows: "Næstu 5 fundir notandans",
    action_items: "Verkefni",
    action_itemsShows: "Undirritanir, óvirk ferli, PDF-skjöl sem mistókust",
    recent_activity: "Nýleg virkni",
    recent_activityShows: "Atburðir í líftíma ferla",
    signatures_expiring: "Undirritanir að renna út",
    signatures_expiringShows: "Undirritunartenglar sem renna út innan 48 klukkustunda",
    cash_to_invest: "Reiðufé til fjárfestingar",
    cash_to_investShows: "Viðskiptavinir með meira reiðufé en viðmiðunarmörkin",
    approvals_waiting: "Bíður samþykkis",
    approvals_waitingShows: "Ferli stöðvuð við regluvörslueftirlit",
    review_status: "Staða yfirferðar",
    review_statusShows: "Lokin ráðgjöf eftir regluvörsluathugun",
    advice_to_check: "Ráðgjöf til athugunar",
    advice_to_checkShows: "Lokin ferli sem hafa ekki enn verið samþykkt",
    screening: "Skimun",
    screeningShows: "Staða PEP og þvingana",
    verified_id: "Staðfest auðkenni",
    verified_idShows: "Viðskiptavinir með og án staðfests auðkennis",
    identity_changes: "Breytingar á auðkenni",
    identity_changesShows: "Breytingar sem ógilda staðfest auðkenni og skimun",
  },
};
